/**
 * Buildings Cost Layer — incremental costs vs BAU.
 * Sits on top of physical pipeline: BAU → efficiency → electrification → RNG → grid → emissions.
 * Costs are downstream only; they do not affect physical outcomes.
 *
 * Placeholder policy-analysis assumptions (fuel prices, capital costs, financial) are centralized
 * here and can be refined later with real data or scenario-specific inputs.
 *
 * Simplifications (v1):
 * - Demand response: annual kWh reduction only, not full load-shape economics.
 * - Commercial growth: same simplified growth as efficiency model if no series.
 * - Other/unmodeled efficiency cost: simple proxy ($10k per % point).
 * - Retail electricity and gas prices: flat real values (no time series).
 * - Avoided BAU fossil replacement cost: zero (TODO).
 * - Panel upgrade: not applied in capex by default; can be applied to a share of electrification cases if needed.
 */

import type { PolicyScenario } from './policyImpactService';
import type { BaselineProjections } from './policyBaselineCache';
import type { TimeSeriesEmissionData } from './policyTimeSeriesService';
import { getBuildingEnergyTimeSeries } from './policyTimeSeriesService';
import type { EfficiencyLayerOptions } from './buildingsEfficiencyModel';
import type { BuildingsBauScenario, BuildingsBauPathway } from './buildingsBauPathways';
import { getDefaultEnergyEfficiencyComponents, hasAnyEfficiencyComponent } from '../types/policyImpact';
import {
  computeBuildingsCostBetweenPathways,
  type BuildingsPathway,
  type BuildingsRetrofitContext,
} from './pathwayCostModel';
import { computeLocationBuildingEnergyIntensity } from './buildingEnergyIntensity';

// ============== ASSUMPTIONS (placeholder; document simplifications) ==============
/**
 * Placeholder policy-analysis assumptions. Refine with real data or scenario-specific inputs.
 * Documented simplifications: flat real prices, single capital values, no avoided BAU replacement (TODO).
 */

// ----- Fuel prices (flat real $; simplification: no time series or location-specific) -----
// Heat pump conversion: electricityAdded_kwh = thermsReplaced * (29.3 / COP). At 40¢/kWh and COP 3, 1 therm → 29.3/3 ≈ 9.77 kWh → $3.91 vs $2.50/therm gas → electrification increases operating cost.
export const ELECTRICITY_PRICE_PER_KWH = 0.40; // 40 cents per kWh
export const NATURAL_GAS_PRICE_PER_THERM = 2.5; // $ per therm

// ----- Capital costs (incremental or program; simplification: single values) -----
export const WEATHERIZATION_COST_PER_PARTICIPATING_HOME = 8000;
/** Placeholder: program cost per participating establishment (existing-building retrofit policy). */
export const COMMERCIAL_RETROFIT_COST_PER_PARTICIPATING_ESTABLISHMENT = 28000;
export const RES_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT = 5000;
export const COM_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT_OR_EQUIVALENT = 15000;
/** Residential heat pump cost per converted unit ($). Capex = converted_units × this. */
export const RES_HEAT_PUMP_COST_PER_UNIT = 10000;
/** Commercial heat pump cost per converted equivalent unit ($). Capex = converted_units × this. */
export const COM_HEAT_PUMP_COST_PER_UNIT = 50000;
/** Avg therms per residential unit per year (for commercial equivalent-unit proxy). */
export const AVG_THERMS_PER_RES_UNIT = 400;
/** Avg therms per commercial equivalent unit per year. */
export const AVG_THERMS_PER_COM_EQUIVALENT_UNIT = 3000;
/** Default heat pump COP for therm→kWh conversion: kWh_per_therm = 29.3 / COP. */
export const DEFAULT_HEAT_PUMP_COP = 3.0;
/** Panel upgrade cost; applied to a share of electrification cases if needed (simplification). */
export const PANEL_UPGRADE_COST = 3000;
/** Demand response: annual program cost per % of building kWh reduced (simplified; not full load-shape). */
export const DEMAND_RESPONSE_PROGRAM_COST_PER_PERCENT_KWH_REDUCED = 50000;

// ----- Financial -----
export const DISCOUNT_RATE = 0.03;
export const ANALYSIS_YEARS = 23; // 2023–2045
export const DEFAULT_ASSET_LIFE_YEARS = 20;

// ----- Incentives (reduce net capex) -----
export const HEAT_PUMP_REBATE = 2000;
export const WEATHERIZATION_REBATE = 2000;
/** Rebate/incentive subtracted from commercial retrofit gross cost per establishment (same order of magnitude as residential when unset). */
export const COMMERCIAL_RETROFIT_REBATE = 2000;
export const REACH_CODE_REBATE = 0;
export const DR_INCENTIVE = 0;

export interface BuildingsCostAssumptions {
  electricityPricePerKwh?: number;
  naturalGasPricePerTherm?: number;
  weatherizationCostPerHome?: number;
  commercialRetrofitCostPerEstablishment?: number;
  resReachIncrementalCostPerUnit?: number;
  comReachIncrementalCostPerUnit?: number;
  resHeatPumpIncrementalCostPerUnit?: number;
  comHeatPumpIncrementalCostPerUnit?: number;
  avgThermsPerResUnit?: number;
  avgThermsPerComEquivalentUnit?: number;
  panelUpgradeCost?: number;
  demandResponseCostPerPercentKwh?: number;
  discountRate?: number;
  heatPumpRebate?: number;
  weatherizationRebate?: number;
  commercialRetrofitRebate?: number;
}

export function getAssumptions(overrides?: BuildingsCostAssumptions) {
  return {
    electricityPricePerKwh: overrides?.electricityPricePerKwh ?? ELECTRICITY_PRICE_PER_KWH,
    naturalGasPricePerTherm: overrides?.naturalGasPricePerTherm ?? NATURAL_GAS_PRICE_PER_THERM,
    weatherizationCostPerHome: overrides?.weatherizationCostPerHome ?? WEATHERIZATION_COST_PER_PARTICIPATING_HOME,
    commercialRetrofitCostPerEstablishment:
      overrides?.commercialRetrofitCostPerEstablishment ?? COMMERCIAL_RETROFIT_COST_PER_PARTICIPATING_ESTABLISHMENT,
    resReachIncrementalCostPerUnit: overrides?.resReachIncrementalCostPerUnit ?? RES_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT,
    comReachIncrementalCostPerUnit: overrides?.comReachIncrementalCostPerUnit ?? COM_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT_OR_EQUIVALENT,
    resHeatPumpIncrementalCostPerUnit: overrides?.resHeatPumpIncrementalCostPerUnit ?? RES_HEAT_PUMP_COST_PER_UNIT,
    comHeatPumpIncrementalCostPerUnit: overrides?.comHeatPumpIncrementalCostPerUnit ?? COM_HEAT_PUMP_COST_PER_UNIT,
    avgThermsPerResUnit: overrides?.avgThermsPerResUnit ?? AVG_THERMS_PER_RES_UNIT,
    avgThermsPerComEquivalentUnit: overrides?.avgThermsPerComEquivalentUnit ?? AVG_THERMS_PER_COM_EQUIVALENT_UNIT,
    panelUpgradeCost: overrides?.panelUpgradeCost ?? PANEL_UPGRADE_COST,
    demandResponseCostPerPercentKwh: overrides?.demandResponseCostPerPercentKwh ?? DEMAND_RESPONSE_PROGRAM_COST_PER_PERCENT_KWH_REDUCED,
    discountRate: overrides?.discountRate ?? DISCOUNT_RATE,
    heatPumpRebate: overrides?.heatPumpRebate ?? HEAT_PUMP_REBATE,
    weatherizationRebate: overrides?.weatherizationRebate ?? WEATHERIZATION_REBATE,
    commercialRetrofitRebate: overrides?.commercialRetrofitRebate ?? COMMERCIAL_RETROFIT_REBATE,
  };
}

/** Annual series: year and value. */
export interface AnnualSeriesRow {
  year: number;
  value: number;
}

/** Cost breakdown by policy (cumulative CAPEX; rebates netted inside each category where modeled). */
export interface CostBreakdownByPolicy {
  residentialRetrofit: number;
  commercialRetrofit: number;
  /** New residential construction (reach) standards; pathway uses residential only. */
  residentialReach: number;
  electrification: number;
  other: number;
  incentives: number;
}

/**
 * Bill-impact diagnostics for 2045 — clarifies citywide vs residential-attributed deltas and per-home denominators.
 * Citywide bills = all modeled building gas + electricity (residential + commercial). Per-home metrics use housing unit count as denominator where noted.
 */
export interface BuildingBillImpactDetail {
  analysisYear: 2045;
  /** Human-readable scope of citywide bill totals. */
  citywideBillScope: string;
  /** policyBill − bauBill at analysis year (all buildings). Positive = higher annual spend under policy. */
  citywideTotalAnnualBillImpact: number;
  /**
   * Residential share of annual bill change, approximated by applying BAU residential therm share to both therms and kWh in that year.
   * Used for per-home metrics that should not attribute commercial load to housing units.
   */
  residentialAttributedAnnualBillImpact: number;
  /** BAU residential therm fraction of total BAU therms (2045). */
  residentialThermShare2045: number;
  /** max(0, BAU therms − policy therms) in 2045. */
  annualThermsReduced2045: number;
  /** max(0, policy kWh − BAU kWh) in 2045. */
  annualKwhAdded2045: number;
  housingUnits2045: number | null;
  /** Modeled cumulative residential units electrified vs BAU path by end of 2045 (stock share × housing). */
  cumulativeElectrifiedResidentialHomes2045: number;
  /**
   * Citywide bill impact ÷ all housing units — numerator includes commercial buildings; interpret as rough citywide spread, not a true per-household bill.
   */
  citywideAverageBillImpactPerHousingUnitAnnual: number | null;
  citywideAverageBillImpactPerHousingUnitMonthly: number | null;
  /** Residential-attributed bill impact ÷ housing units (2045). */
  averageAcrossAllHomesAnnualBillImpact: number | null;
  averageAcrossAllHomesMonthlyBillImpact: number | null;
  /** Residential-attributed bill impact ÷ cumulative electrified homes when &gt; 0. */
  averageForAffectedHomesAnnualBillImpact: number | null;
  averageForAffectedHomesMonthlyBillImpact: number | null;
  /** Explicit terms for transparency (same units as annual bill impacts, $/yr). */
  citywidePerHousingUnit: { numeratorAnnual: number; denominatorHousingUnits: number | null };
  allHomesResidentialAttributed: { numeratorAnnual: number; denominatorHousingUnits: number | null };
  affectedHomesResidentialAttributed: { numeratorAnnual: number; denominatorElectrifiedHomes: number };
}

/** Cost-effectiveness metrics. */
export interface CostEffectivenessMetrics {
  upfrontCapitalCost: number;
  incrementalCapitalVsBau: number;
  netAnnualOperatingSavings2045: number;
  cumulativeNetCost: number;
  netPresentValue: number;
  /** Undiscounted cumulative net cost / total building MT CO2e reduced; null if no reduction. */
  costPerTonCO2eReduced: number | null;
  /** First calendar year when cumulative net cost ≤ 0; null if no breakeven in analysis period. */
  paybackYear: number | null;
  /** Years from baseline to payback (paybackYear - BASE_YEAR); null if no payback. Kept for compatibility. */
  paybackPeriodYears: number | null;
}

export interface BuildingsCostResults {
  annualCostSeries: AnnualSeriesRow[];
  annualSavingsSeries: AnnualSeriesRow[];
  annualNetCashFlowSeries: AnnualSeriesRow[];
  cumulativeNetCostSeries: AnnualSeriesRow[];
  costBreakdownByPolicy: CostBreakdownByPolicy;
  costEffectivenessMetrics: CostEffectivenessMetrics;
  /** Total building emissions reduction (MT CO₂e) over analysis period (baseline − policy). */
  totalBuildingEmissionsReductionMT: number;
  /** BAU vs policy utility bills by year (for charts). */
  bauBillByYear: AnnualSeriesRow[];
  policyBillByYear: AnnualSeriesRow[];
  /** BAU vs scenario electricity and natural gas cost by year (for fuel comparison chart). */
  bauGasCostByYear: AnnualSeriesRow[];
  bauElecCostByYear: AnnualSeriesRow[];
  policyGasCostByYear: AnnualSeriesRow[];
  policyElecCostByYear: AnnualSeriesRow[];
  /** Annual capital cost by year (for charts). */
  annualCapitalCostSeries: AnnualSeriesRow[];
  /** Debug/validation: 2045 snapshot. */
  debug?: {
    year2045: {
      bauBill: number;
      policyBill: number;
      annualSavings: number;
      annualCapex: number;
      cumulativeNetCost: number;
      costPerTon: number | null;
      paybackYear: number | null;
    };
  };
  /** Electrification $/unit debug (per-year + 2045 totals) when location is Davis. For validation. */
  electrificationDebug?: import('./pathwayCostModel').BuildingsPathwayCostResult['electrificationDebug'];
  /** Operating cost audit: baseline gas, policy gas/elec, incremental cost, electricity added, $/therm electric. */
  operatingCostDebug?: import('./pathwayCostModel').BuildingsPathwayCostResult['operatingCostDebug'];
  /** 2045 bill-impact methodology: citywide vs residential-attributed, therms/kWh, denominators. */
  buildingBillImpactDetail?: BuildingBillImpactDetail;
}

const BASE_YEAR = 2023;
const TARGET_YEAR = 2045;

/** 2045 bill-impact snapshot: citywide vs residential-attributed deltas and explicit per-home denominators. */
function computeBuildingBillImpactDetail2045(params: {
  idx2045: number;
  bauThermsByYear: number[];
  policyThermsByYear: number[];
  bauKwhByYear: number[];
  policyKwhByYear: number[];
  bauResThermsByYear: number[];
  policyResThermsByYear: number[];
  resFracByYear: number[];
  housingUnits2045: number | null;
  citywideTotalAnnualBillImpact: number;
  electricityPricePerKwh: number;
  naturalGasPricePerTherm: number;
  electrificationStock2045?: import('./pathwayCostModel').ElectrificationStock2045;
}): BuildingBillImpactDetail {
  const i = params.idx2045;
  const bauT = params.bauThermsByYear[i] ?? 0;
  const polT = params.policyThermsByYear[i] ?? 0;
  const bauK = params.bauKwhByYear[i] ?? 0;
  const polK = params.policyKwhByYear[i] ?? 0;
  const bauResT = params.bauResThermsByYear[i] ?? 0;
  const polResT = params.policyResThermsByYear[i] ?? 0;
  const resFrac = params.resFracByYear[i] ?? 0.6;
  const Pg = params.naturalGasPricePerTherm;
  const Pe = params.electricityPricePerKwh;

  // Residential share of bill change: actual res therm delta + BAU therm share × total kWh delta (same split rule as CAPEX context).
  const residentialAttributedAnnualBillImpact = Pg * (polResT - bauResT) + Pe * resFrac * (polK - bauK);

  const annualThermsReduced2045 = Math.max(0, bauT - polT);
  const annualKwhAdded2045 = Math.max(0, polK - bauK);
  const housingUnits2045 = params.housingUnits2045;
  const cumulativeElectrifiedResidentialHomes2045 =
    params.electrificationStock2045?.cumulativeElectrifiedResidentialUnits ?? 0;

  const citywide = params.citywideTotalAnnualBillImpact;

  const citywidePerHU =
    housingUnits2045 != null && housingUnits2045 > 0 ? citywide / housingUnits2045 : null;
  const allHomesAvg =
    housingUnits2045 != null && housingUnits2045 > 0
      ? residentialAttributedAnnualBillImpact / housingUnits2045
      : null;
  const affectedAvg =
    cumulativeElectrifiedResidentialHomes2045 > 0
      ? residentialAttributedAnnualBillImpact / cumulativeElectrifiedResidentialHomes2045
      : null;

  return {
    analysisYear: 2045,
    citywideBillScope: 'All modeled building gas and electricity (residential + commercial).',
    citywideTotalAnnualBillImpact: citywide,
    residentialAttributedAnnualBillImpact,
    residentialThermShare2045: resFrac,
    annualThermsReduced2045,
    annualKwhAdded2045,
    housingUnits2045,
    cumulativeElectrifiedResidentialHomes2045,
    citywideAverageBillImpactPerHousingUnitAnnual: citywidePerHU,
    citywideAverageBillImpactPerHousingUnitMonthly: citywidePerHU != null ? citywidePerHU / 12 : null,
    averageAcrossAllHomesAnnualBillImpact: allHomesAvg,
    averageAcrossAllHomesMonthlyBillImpact: allHomesAvg != null ? allHomesAvg / 12 : null,
    averageForAffectedHomesAnnualBillImpact: affectedAvg,
    averageForAffectedHomesMonthlyBillImpact: affectedAvg != null ? affectedAvg / 12 : null,
    citywidePerHousingUnit: {
      numeratorAnnual: citywide,
      denominatorHousingUnits: housingUnits2045,
    },
    allHomesResidentialAttributed: {
      numeratorAnnual: residentialAttributedAnnualBillImpact,
      denominatorHousingUnits: housingUnits2045,
    },
    affectedHomesResidentialAttributed: {
      numeratorAnnual: residentialAttributedAnnualBillImpact,
      denominatorElectrifiedHomes: cumulativeElectrifiedResidentialHomes2045,
    },
  };
}

export interface ComputeBuildingsCostResultsParams {
  location: string;
  baseline: BaselineProjections;
  policyScenario: PolicyScenario;
  buildingsBauScenario: BuildingsBauScenario;
  buildingsRngPercentOverride?: number;
  buildingsCop?: number;
  efficiencyLayerOptions?: EfficiencyLayerOptions;
  /** Baseline and projected time series (for building emissions and reduction). */
  emissionsResults: {
    baselineData: TimeSeriesEmissionData[];
    projectedData: TimeSeriesEmissionData[];
  };
  assumptions?: BuildingsCostAssumptions;
  /** When set, compare costs vs this pathway (e.g. flat baseline) instead of BAU. */
  referencePathway?: BuildingsBauPathway;
  /** When referencePathway is set: compare to BAU (state policy) or to policy (state + local). */
  comparisonTarget?: 'bau' | 'policy';
  /** Residential stock proxy by year for electrification CAPEX ($/unit). When omitted, derived from population/2.5. */
  housingUnitsByYear?: Record<number, number>;
  /**
   * From `business_establishments`: sector "Total for all sectors" + size "All establishments".
   * When set, commercial energy is scaled per establishment instead of population÷6.
   */
  commercialEstablishmentCount?: number | null;
}

/**
 * Build BuildingsPathway from therms/kwh arrays and optional reach %.
 */
function toBuildingsPathway(
  years: number[],
  thermsByYear: number[],
  kwhByYear: number[],
  resReachPercent = 0,
  comReachPercent = 0
): BuildingsPathway {
  return {
    years,
    thermsByYear,
    kwhByYear,
    resReachCodePercent: resReachPercent,
    comReachCodePercent: comReachPercent,
  };
}

/** Build zeroed buildings cost result for given years (used when no local policy). */
function zeroedBuildingsCostResult(years: number[]): BuildingsCostResults {
  const zeroSeries = years.map((y) => ({ year: y, value: 0 }));
  return {
    annualCostSeries: zeroSeries,
    annualSavingsSeries: zeroSeries,
    annualNetCashFlowSeries: zeroSeries,
    cumulativeNetCostSeries: zeroSeries,
    costBreakdownByPolicy: {
      residentialRetrofit: 0,
      commercialRetrofit: 0,
      residentialReach: 0,
      electrification: 0,
      other: 0,
      incentives: 0,
    },
    costEffectivenessMetrics: {
      upfrontCapitalCost: 0,
      incrementalCapitalVsBau: 0,
      netAnnualOperatingSavings2045: 0,
      cumulativeNetCost: 0,
      netPresentValue: 0,
      costPerTonCO2eReduced: null,
      paybackYear: null,
      paybackPeriodYears: null,
    },
    totalBuildingEmissionsReductionMT: 0,
    bauBillByYear: zeroSeries,
    policyBillByYear: zeroSeries,
    bauGasCostByYear: zeroSeries,
    bauElecCostByYear: zeroSeries,
    policyGasCostByYear: zeroSeries,
    policyElecCostByYear: zeroSeries,
    annualCapitalCostSeries: zeroSeries,
    buildingBillImpactDetail: undefined,
  };
}

/**
 * Compute buildings cost using unified pathway-difference. Same formula for all views; only baseline and policy pathways differ.
 * When in incremental_vs_bau and there is no meaningful building emissions reduction (baseline ≈ policy), returns zeroed results.
 */
export function computeBuildingsCostResults(params: ComputeBuildingsCostResultsParams): BuildingsCostResults {
  try {
    return computeBuildingsCostResultsInner(params);
  } catch (err) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.error('[buildingsCostModel] computeBuildingsCostResults error:', err);
    }
    const years = [2023];
    for (let y = 2024; y <= 2045; y++) years.push(y);
    return zeroedBuildingsCostResult(years);
  }
}

function computeBuildingsCostResultsInner(params: ComputeBuildingsCostResultsParams): BuildingsCostResults {
  const {
    baseline,
    policyScenario,
    efficiencyLayerOptions,
    emissionsResults,
    assumptions: assumptionOverrides,
  } = params;

  const debugBe =
    typeof process !== 'undefined' &&
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_DEBUG_BUILDING_ELECTRIFICATION === '1';

  const pop = baseline.populationData ?? {};
  const pop2023 = pop[BASE_YEAR] ?? baseline.population ?? 0;
  const housingForIntensity =
    params.housingUnitsByYear?.[BASE_YEAR] != null && params.housingUnitsByYear[BASE_YEAR]! > 0
      ? params.housingUnitsByYear[BASE_YEAR]!
      : Math.max(0, pop2023 / 2.5);

  const intensity = computeLocationBuildingEnergyIntensity(baseline.buildingsEnergyData, BASE_YEAR, {
    population: pop2023,
    housingUnits: housingForIntensity,
    commercialEstablishmentCount:
      params.commercialEstablishmentCount != null && params.commercialEstablishmentCount > 0
        ? params.commercialEstablishmentCount
        : null,
  });

  const mergedAssumptionOverrides: BuildingsCostAssumptions = {
    ...assumptionOverrides,
    ...(intensity.dataSource === 'location'
      ? {
          avgThermsPerResUnit: intensity.residentialThermsPerHousehold,
          avgThermsPerComEquivalentUnit: intensity.avgThermsPerCommercialEquivalentUnit,
        }
      : {}),
  };
  const assumptions = getAssumptions(mergedAssumptionOverrides);


  const energy = getBuildingEnergyTimeSeries(baseline, policyScenario, {
    efficiencyLayerOptions,
    cop: params.buildingsCop ?? DEFAULT_HEAT_PUMP_COP,
    rngOverridePercent: params.buildingsRngPercentOverride,
    debugEnergyPrices: debugBe
      ? {
          electricityPricePerKwh: assumptions.electricityPricePerKwh,
          naturalGasPricePerTherm: assumptions.naturalGasPricePerTherm,
        }
      : undefined,
    debugLocationLabel: debugBe ? params.location : undefined,
  });

  const {
    years,
    bauThermsByYear,
    bauKwhByYear,
    policyThermsByYear,
    policyKwhByYear,
    bauResThermsByYear,
    bauComThermsByYear,
    policyResThermsByYear,
  } = energy;
  const components = policyScenario.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents();

  const referencePathway = params.referencePathway;
  const comparisonTarget = params.comparisonTarget ?? 'policy';

  // When user has no local building policy (0% electrification, 0% efficiency), return zeros immediately.
  // Prevents phantom spikes when only EV adoption slider is changed; building cost is invariant to EV.
  const hasElectrification = (policyScenario.heatPumpAdoptionPercent ?? 0) > 0;
  const hasEfficiency = hasAnyEfficiencyComponent(components);
  if (!referencePathway && !hasElectrification && !hasEfficiency) {
    return zeroedBuildingsCostResult(years);
  }

  // For incremental_vs_bau: if baseline and policy building energy (therms + kWh) are effectively identical, return zeros.
  // Uses source-of-truth energy series (never affected by EV); robust to stale emissions or floating-point noise.
  if (!referencePathway) {
    const energyIdentical =
      bauThermsByYear.length === policyThermsByYear.length &&
      bauKwhByYear.length === policyKwhByYear.length &&
      years.every((_, i) => {
        const dt = Math.abs((bauThermsByYear[i] ?? 0) - (policyThermsByYear[i] ?? 0));
        const dk = Math.abs((bauKwhByYear[i] ?? 0) - (policyKwhByYear[i] ?? 0));
        const tol = 1e-6;
        return dt < tol && dk < tol;
      });
    if (energyIdentical) {
      return zeroedBuildingsCostResult(years);
    }
  }

  let baselinePathway: BuildingsPathway;
  let policyPathway: BuildingsPathway;

  if (referencePathway) {
    baselinePathway = toBuildingsPathway(
      referencePathway.years,
      referencePathway.thermsByYear,
      referencePathway.kwhByYear,
      0,
      0
    );
    policyPathway =
      comparisonTarget === 'bau'
        ? toBuildingsPathway(years, bauThermsByYear, bauKwhByYear, 0, 0)
        : toBuildingsPathway(
            years,
            policyThermsByYear,
            policyKwhByYear,
            components.residentialReachCodePercent ?? 0,
            0
          );
  } else {
    baselinePathway = toBuildingsPathway(years, bauThermsByYear, bauKwhByYear, 0, 0);
    policyPathway = toBuildingsPathway(
      years,
      policyThermsByYear,
      policyKwhByYear,
      components.residentialReachCodePercent ?? 0,
      0
    );
  }

  // Electrification context for $/unit CAPEX: baseline vs policy res/com therms and housing
  const housingUnitsByYear: Record<number, number> = { ...(params.housingUnitsByYear ?? {}) };
  for (const y of years) {
    if (housingUnitsByYear[y] == null || housingUnitsByYear[y] <= 0) {
      housingUnitsByYear[y] = Math.max(0, (pop[y] ?? pop2023) / 2.5);
    }
  }
  const resFracByYear = years.map((_, i) => {
    const tot = (bauResThermsByYear[i] ?? 0) + (bauComThermsByYear[i] ?? 0);
    return tot > 0 ? (bauResThermsByYear[i] ?? 0) / tot : 0.6;
  });
  const baselineResByYear: Record<number, number> = {};
  const baselineComByYear: Record<number, number> = {};
  const policyResByYear: Record<number, number> = {};
  const policyComByYear: Record<number, number> = {};
  for (let i = 0; i < years.length; i++) {
    const y = years[i];
    const idxBase = baselinePathway.years.indexOf(y);
    const idxPol = policyPathway.years.indexOf(y);
    const tBase = idxBase >= 0 ? (baselinePathway.thermsByYear[idxBase] ?? 0) : 0;
    const tPol = idxPol >= 0 ? (policyPathway.thermsByYear[idxPol] ?? 0) : 0;
    baselineResByYear[y] = tBase * resFracByYear[i];
    baselineComByYear[y] = tBase * (1 - resFracByYear[i]);
    policyResByYear[y] = tPol * resFracByYear[i];
    policyComByYear[y] = tPol * (1 - resFracByYear[i]);
  }

  const electrificationContext = {
    housingUnitsByYear,
    baselineResThermsByYear: baselineResByYear,
    policyResThermsByYear: policyResByYear,
    baselineComThermsByYear: baselineComByYear,
    policyComThermsByYear: policyComByYear,
    resHeatPumpIncrementalCostPerUnit: assumptions.resHeatPumpIncrementalCostPerUnit,
    comHeatPumpIncrementalCostPerUnit: assumptions.comHeatPumpIncrementalCostPerUnit,
    avgThermsPerResUnit: Math.max(1, assumptions.avgThermsPerResUnit),
    avgThermsPerComEquivalentUnit: Math.max(1, assumptions.avgThermsPerComEquivalentUnit),
    heatPumpRebate: assumptions.heatPumpRebate,
    hasElectrification: (policyScenario.heatPumpAdoptionPercent ?? 0) > 0,
  };

  const includeElectrificationDebug =
    typeof params.location === 'string' && params.location.toLowerCase().includes('davis');

  const includeRetrofitCapex = !referencePathway || comparisonTarget === 'policy';
  const housing2023 =
    housingUnitsByYear[BASE_YEAR] != null && housingUnitsByYear[BASE_YEAR]! > 0
      ? housingUnitsByYear[BASE_YEAR]!
      : Math.max(0, pop2023 / 2.5);
  const establishments2023 =
    params.commercialEstablishmentCount != null && params.commercialEstablishmentCount > 0
      ? params.commercialEstablishmentCount
      : Math.max(0, intensity.commercialEquivalentUnits);

  const retrofitContext: BuildingsRetrofitContext | undefined = includeRetrofitCapex
    ? {
        housingUnits2023: housing2023,
        establishments2023: establishments2023,
        residentialRetrofitPercent2045: components.residentialRetrofitExistingPercent ?? 0,
        commercialRetrofitPercent2045: components.commercialRetrofitExistingPercent ?? 0,
      }
    : undefined;

  const pathwayResult = computeBuildingsCostBetweenPathways(
    baselinePathway,
    policyPathway,
    mergedAssumptionOverrides,
    electrificationContext,
    includeElectrificationDebug,
    retrofitContext
  );

  const buildingEmissionsMT = (row: TimeSeriesEmissionData) => (row.naturalGasEmissions ?? 0) + (row.electricityEmissions ?? 0);
  const baselineTotalByYear = emissionsResults.baselineData
    .filter((r) => r.year >= BASE_YEAR && r.year <= TARGET_YEAR)
    .map((r) => ({ year: r.year, mt: buildingEmissionsMT(r) }));
  const policyTotalByYear = emissionsResults.projectedData
    .filter((r) => r.year >= BASE_YEAR && r.year <= TARGET_YEAR)
    .map((r) => ({ year: r.year, mt: buildingEmissionsMT(r) }));
  const totalReductionMT = years.reduce((sum, y) => {
    const b = baselineTotalByYear.find((r) => r.year === y)?.mt ?? 0;
    const p = policyTotalByYear.find((r) => r.year === y)?.mt ?? 0;
    return sum + Math.max(0, b - p);
  }, 0);

  const costPerTonCO2eReduced =
    totalReductionMT > 0 ? pathwayResult.cumulativeNetCost / totalReductionMT : null;
  const idx2045 = years.indexOf(TARGET_YEAR);


  const annualSavingsSeries: AnnualSeriesRow[] = pathwayResult.annualSavingsSeries.map((r) => ({ year: r.year, value: r.value }));
  const annualCapitalCostSeries: AnnualSeriesRow[] = pathwayResult.annualCapexSeries.map((r) => ({ year: r.year, value: r.value }));
  // Cash-flow sign convention for UI:
  // - Savings are positive when BAU operating cost > policy operating cost.
  // - Capital exp. is non-negative (cost outflow).
  // Net = operating impact (savings) - capex, so positive values mean net savings.
  const annualNetCashFlowSeries: AnnualSeriesRow[] = years.map((y, i) => ({
    year: y,
    value: annualSavingsSeries[i].value - annualCapitalCostSeries[i].value,
  }));
  const cumulativeNetCostSeries: AnnualSeriesRow[] = pathwayResult.cumulativeNetCostSeries.map((r) => ({ year: r.year, value: r.value }));

  const costBreakdownByPolicy: CostBreakdownByPolicy = {
    residentialRetrofit: pathwayResult.residentialRetrofitCapex,
    commercialRetrofit: pathwayResult.commercialRetrofitCapex,
    residentialReach: pathwayResult.reachCapex,
    electrification: pathwayResult.electrificationCapex,
    other: 0,
    incentives: 0,
  };

  const netAnnualOperatingSavings2045 = idx2045 >= 0 ? annualSavingsSeries[idx2045]?.value : 0;

  const costEffectivenessMetrics: CostEffectivenessMetrics = {
    upfrontCapitalCost: pathwayResult.upfrontCapitalCost,
    incrementalCapitalVsBau: pathwayResult.upfrontCapitalCost,
    netAnnualOperatingSavings2045,
    cumulativeNetCost: pathwayResult.cumulativeNetCost,
    netPresentValue: pathwayResult.netPresentValue,
    costPerTonCO2eReduced: costPerTonCO2eReduced ?? null,
    paybackYear: pathwayResult.paybackYear,
    paybackPeriodYears: pathwayResult.paybackYear != null ? pathwayResult.paybackYear - BASE_YEAR : null,
  };

  const bauBillByYear: AnnualSeriesRow[] = pathwayResult.baselineCostByYear.map((r) => ({ year: r.year, value: r.value }));
  const policyBillByYear: AnnualSeriesRow[] = pathwayResult.policyCostByYear.map((r) => ({ year: r.year, value: r.value }));

  const debug =
    idx2045 >= 0
      ? {
          year2045: {
            bauBill: bauBillByYear[idx2045]?.value ?? 0,
            policyBill: policyBillByYear[idx2045]?.value ?? 0,
            annualSavings: annualSavingsSeries[idx2045]?.value ?? 0,
            annualCapex: annualCapitalCostSeries[idx2045]?.value ?? 0,
            cumulativeNetCost: cumulativeNetCostSeries[idx2045]?.value ?? 0,
            costPerTon: costPerTonCO2eReduced,
            paybackYear: pathwayResult.paybackYear,
          },
        }
      : undefined;

  // Berkeley cost debug: expose by-year series for troubleshooting (flat vs ramping, sign of savings).
  const isBerkeley = typeof params.location === 'string' && params.location.toLowerCase().includes('berkeley');
  const housingUnits2045Raw = housingUnitsByYear[TARGET_YEAR];
  const housingUnits2045ForDetail: number | null =
    housingUnits2045Raw != null && housingUnits2045Raw > 0 ? housingUnits2045Raw : null;
  const citywideBillDelta2045 =
    idx2045 >= 0
      ? (policyBillByYear[idx2045]?.value ?? 0) - (bauBillByYear[idx2045]?.value ?? 0)
      : 0;
  const buildingBillImpactDetail: BuildingBillImpactDetail | undefined =
    idx2045 >= 0
      ? computeBuildingBillImpactDetail2045({
          idx2045,
          bauThermsByYear,
          policyThermsByYear,
          bauKwhByYear,
          policyKwhByYear,
          bauResThermsByYear,
          policyResThermsByYear,
          resFracByYear,
          housingUnits2045: housingUnits2045ForDetail,
          citywideTotalAnnualBillImpact: citywideBillDelta2045,
          electricityPricePerKwh: assumptions.electricityPricePerKwh,
          naturalGasPricePerTherm: assumptions.naturalGasPricePerTherm,
          electrificationStock2045: pathwayResult.electrificationStock2045,
        })
      : undefined;

  return {
    annualCostSeries: annualCapitalCostSeries.map((row) => ({ ...row, value: row.value })),
    annualSavingsSeries,
    annualNetCashFlowSeries,
    cumulativeNetCostSeries,
    costBreakdownByPolicy,
    costEffectivenessMetrics,
    totalBuildingEmissionsReductionMT: totalReductionMT,
    bauBillByYear,
    policyBillByYear,
    bauGasCostByYear: pathwayResult.baselineGasCostByYear.map((r) => ({ year: r.year, value: r.value })),
    bauElecCostByYear: pathwayResult.baselineElecCostByYear.map((r) => ({ year: r.year, value: r.value })),
    policyGasCostByYear: pathwayResult.policyGasCostByYear.map((r) => ({ year: r.year, value: r.value })),
    policyElecCostByYear: pathwayResult.policyElecCostByYear.map((r) => ({ year: r.year, value: r.value })),
    annualCapitalCostSeries,
    debug,
    electrificationDebug: pathwayResult.electrificationDebug,
    operatingCostDebug: pathwayResult.operatingCostDebug,
    buildingBillImpactDetail,
  };
}
